/**
 * Stub creation for large query data.
 * Creates lightweight schema representations when data exceeds size limits.
 */

import { formatSize, getArrayFields, hasObjectElements } from './helpers';

import type { QueryStub } from './types';
import type { QueryKey } from '@tanstack/react-query';

/**
 * Recursively inspect a value and return a lightweight schema representation.
 * Doesn't include full nested data, just metadata about structure.
 *
 * @param value - The value to inspect (unknown type for safety)
 * @param depth - Current recursion depth
 * @param maxDepth - Maximum depth to traverse
 * @returns Schema representation of the value
 */
export function inspectValue(value: unknown, depth = 0, maxDepth = 2): unknown {
  if (depth > maxDepth) {
    return { _type: typeof value, _truncated: true };
  }

  // Null/undefined
  if (value === null) {
    return null;
  }
  if (value === undefined) {
    return undefined;
  }

  // Primitives - include actual value (but truncate long strings)
  if (typeof value === 'string') {
    return value.length > 50 ? `${value.substring(0, 50)}... (${value.length} chars)` : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  // Arrays - show count and inspect first item only
  if (Array.isArray(value)) {
    return {
      _type: 'array',
      _count: value.length,
      _sample: value.length > 0 ? inspectValue(value[0], depth + 1, maxDepth) : null,
    };
  }

  // Objects - show field names and inspect a few key fields
  if (typeof value === 'object') {
    const keys = Object.keys(value);
    const schema: Record<string, unknown> = {
      _type: 'object',
      _fields: keys,
    };

    // Include actual values for first 3 fields only (to keep it small)
    const sampleKeys = keys.slice(0, 3);
    sampleKeys.forEach((key) => {
      schema[key] = inspectValue((value as Record<string, unknown>)[key], depth + 1, maxDepth);
    });

    if (keys.length > 3) {
      schema._additionalFields = keys.length - 3;
    }

    return schema;
  }

  return { _type: typeof value };
}

/**
 * Create a stub representation of large data.
 * Extracts schema and minimal sample data while keeping size small.
 *
 * @param queryKey - React Query key for this data
 * @param data - The query data to create a stub for (unknown for safety)
 * @param originalSize - Size of the original data in bytes
 * @returns A stub representation with schema information
 */
export function createStub(queryKey: QueryKey, data: unknown, originalSize: number): QueryStub {
  const stub: QueryStub = {
    _stub: true,
    _realSize: formatSize(originalSize),
    _queryKey: queryKey,
    _message: 'This is a stub. Use request_additional_context action to load specific data.',
    _schema: null,
  };

  // Handle array data (most common case)
  if (Array.isArray(data)) {
    stub._schema = {
      type: 'array',
      recordCount: data.length,
      fields: getArrayFields(data),
      sampleStructure: data.length > 0 ? inspectValue(data[0], 0, 2) : null,
    };
  }
  // Handle object data
  else if (typeof data === 'object' && data !== null) {
    const dataObj = data as Record<string, unknown>;

    // Check if it's a paginated response with a data array
    if ('data' in dataObj && Array.isArray(dataObj.data)) {
      stub._schema = {
        type: 'paginated',
        recordCount: dataObj.data.length,
        fields: getArrayFields(dataObj.data),
        sampleStructure: hasObjectElements(dataObj.data) ? inspectValue(dataObj.data[0], 0, 2) : null,
        // Include pagination metadata
        pagination: Object.keys(dataObj)
          .filter((k) => k !== 'data')
          .reduce<Record<string, unknown>>((acc, k) => {
            const value = dataObj[k];
            // Only include simple values, not large nested objects
            if (typeof value !== 'object' || value === null) {
              return { ...acc, [k]: value };
            }
            return { ...acc, [k]: { _type: typeof value } };
          }, {}),
      };
    } else {
      stub._schema = {
        type: 'object',
        fields: Object.keys(dataObj),
        sampleStructure: inspectValue(data, 0, 1),
      };
    }
  } else {
    stub._schema = {
      type: typeof data,
      value: String(data).substring(0, 100),
    };
  }

  return stub;
}
